import { executeQuery, updateRecord } from './salesforce/client';
import { getPicklistValues } from './salesforce/describe';
import { getCached, setCache } from './cache';
import {
  EXCLUDE_DEACTIVATED_ACCOUNTS,
  EXCLUDE_DEACTIVATED_ACCOUNTS_REL,
  EXCLUDE_DEMO_DEACTIVATED_USERS,
} from '../constants';
import type {
  ChurnAnalysis,
  ChurnAccount,
  StaleUsersData,
  StaleUser,
  StaleAccountsData,
  StaleAccount,
} from '../types/retention';

const CACHE_TTL = 15 * 60 * 1000; // 15 minutes
const CHURN_CACHE_KEY = 'retention_churn_analysis';

async function cachedQuery<T>(
  cacheKey: string,
  fetcher: () => Promise<T>,
): Promise<T> {
  const cached = getCached<T>(cacheKey, CACHE_TTL);
  if (cached !== null) return cached;
  const result = await fetcher();
  setCache(cacheKey, result);
  return result;
}

function daysSince(dateStr: string): number {
  const then = new Date(dateStr);
  const now = new Date();
  return Math.floor((now.getTime() - then.getTime()) / (1000 * 60 * 60 * 24));
}

function latestDate(dates: (string | null)[]): string | null {
  const valid = dates.filter((d): d is string => d != null);
  if (valid.length === 0) return null;
  return valid.reduce((a, b) => (a > b ? a : b));
}

/**
 * Normalize a Salesforce date or datetime value to a date-only string
 * (YYYY-MM-DD). Account activity fields are a mix of Date and DateTime
 * types, so this keeps the displayed column consistent.
 */
function toDateOnly(dateStr: string | null): string | null {
  if (!dateStr) return null;
  return dateStr.slice(0, 10);
}

// ── Churn / Pre-Churn Analysis ──

const CHURN_DAYS = 180;
const PRE_CHURN_DAYS = 90;

export async function fetchChurnAnalysis(signal?: AbortSignal): Promise<ChurnAnalysis> {
  return cachedQuery(CHURN_CACHE_KEY, async () => {
    // Query 1: Accounts with 7+ plans
    const accounts = await executeQuery<{
      Id: string;
      Name: string;
      Total_Plans_Sold__c: number | null;
      Last_Plan_Date__c: string | null;
      Last_Spend_Date__c: string | null;
      EEP_Trust_Based_Plan_Credits__c: number | null;
      EEP_Will_Based_Plan_Credits__c: number | null;
      Churn_Reason__c: string | null;
      Website: string | null;
      EEP_Firm_ID__c: string | null;
    }>(
      `SELECT Id, Name, Total_Plans_Sold__c, Last_Plan_Date__c, Last_Spend_Date__c, EEP_Trust_Based_Plan_Credits__c, EEP_Will_Based_Plan_Credits__c, Churn_Reason__c, Website, EEP_Firm_ID__c FROM Account WHERE Total_Plans_Sold__c >= 7 AND EEP_Firm_ID__c != null${EXCLUDE_DEACTIVATED_ACCOUNTS}`,
      signal,
    );

    // Query 2: Most recent Opportunity dates per account
    const oppRecords = await executeQuery<{
      AccountId: string;
      Plan_Created_Date__c: string | null;
      Submitted_Date__c: string | null;
    }>(
      `SELECT AccountId, Plan_Created_Date__c, Submitted_Date__c FROM Opportunity WHERE Account.Total_Plans_Sold__c >= 7 AND Account.EEP_Firm_ID__c != null AND (Plan_Created_Date__c != null OR Submitted_Date__c != null)${EXCLUDE_DEACTIVATED_ACCOUNTS_REL}`,
      signal,
    );

    // Build max opp dates per account
    const oppDateMap = new Map<string, { maxPlanCreated: string | null; maxSubmitted: string | null }>();
    for (const opp of oppRecords) {
      if (!opp.AccountId) continue;
      const existing = oppDateMap.get(opp.AccountId);
      if (existing) {
        existing.maxPlanCreated = latestDate([existing.maxPlanCreated, opp.Plan_Created_Date__c]);
        existing.maxSubmitted = latestDate([existing.maxSubmitted, opp.Submitted_Date__c]);
      } else {
        oppDateMap.set(opp.AccountId, {
          maxPlanCreated: opp.Plan_Created_Date__c,
          maxSubmitted: opp.Submitted_Date__c,
        });
      }
    }

    const churnAccounts: ChurnAccount[] = [];
    const preChurnAccounts: ChurnAccount[] = [];

    for (const r of accounts) {
      const totalPlans = r.Total_Plans_Sold__c || 0;
      const oppDates = oppDateMap.get(r.Id);
      const lastOppPlanCreatedDate = oppDates?.maxPlanCreated ?? null;
      const lastOppSubmittedDate = oppDates?.maxSubmitted ?? null;

      const lastActivity = latestDate([
        r.Last_Plan_Date__c,
        r.Last_Spend_Date__c,
        lastOppPlanCreatedDate,
        lastOppSubmittedDate,
      ]);

      // All dates null → not churned
      if (!lastActivity) continue;

      const days = daysSince(lastActivity);

      const shared = {
        id: r.Id,
        name: r.Name,
        totalPlans,
        lastActivityDate: toDateOnly(lastActivity),
        daysSinceActivity: days,
        lastPlanDate: r.Last_Plan_Date__c,
        lastSpendDate: r.Last_Spend_Date__c,
        lastOppPlanCreatedDate,
        lastOppSubmittedDate,
        trustBasedCredits: r.EEP_Trust_Based_Plan_Credits__c,
        willBasedCredits: r.EEP_Will_Based_Plan_Credits__c,
        churnReason: r.Churn_Reason__c ?? null,
        website: r.Website,
        eepFirmId: r.EEP_Firm_ID__c,
      };

      if (days >= CHURN_DAYS) {
        churnAccounts.push(shared);
      } else if (days >= PRE_CHURN_DAYS) {
        preChurnAccounts.push(shared);
      }
    }

    // Sort by days since activity descending (most stale first)
    churnAccounts.sort((a, b) => b.daysSinceActivity - a.daysSinceActivity);
    preChurnAccounts.sort((a, b) => b.daysSinceActivity - a.daysSinceActivity);

    return {
      churn: { total: churnAccounts.length, accounts: churnAccounts },
      preChurn: { total: preChurnAccounts.length, accounts: preChurnAccounts },
    };
  });
}

/** Active picklist values for Account.Churn_Reason__c (for the editable dropdown). */
export async function fetchChurnReasonOptions(signal?: AbortSignal): Promise<string[]> {
  return getPicklistValues('Account', 'Churn_Reason__c', signal);
}

/** Write a churn reason back to Salesforce in real time. Pass null to clear. */
export async function updateChurnReason(
  accountId: string,
  churnReason: string | null,
): Promise<void> {
  await updateRecord('Account', accountId, { Churn_Reason__c: churnReason });

  // Reconcile the cached analysis so a revisit within the TTL shows the new
  // value instead of the pre-edit one.
  const cached = getCached<ChurnAnalysis>(CHURN_CACHE_KEY, CACHE_TTL);
  if (cached) {
    const patch = (a: ChurnAccount): ChurnAccount =>
      a.id === accountId ? { ...a, churnReason } : a;
    setCache(CHURN_CACHE_KEY, {
      churn: { ...cached.churn, accounts: cached.churn.accounts.map(patch) },
      preChurn: { ...cached.preChurn, accounts: cached.preChurn.accounts.map(patch) },
    });
  }
}

/**
 * Returns one record per churned account with a computed churn date
 * (lastActivity + 180 days) for use by the weekly metrics bucketing system.
 */
export async function fetchChurnRecords(
  startDate: string,
  signal?: AbortSignal,
): Promise<Record<string, unknown>[]> {
  const accounts = await executeQuery<{
    Id: string;
    Name: string;
    Total_Plans_Sold__c: number | null;
    Last_Plan_Date__c: string | null;
    Last_Spend_Date__c: string | null;
  }>(
    `SELECT Id, Name, Total_Plans_Sold__c, Last_Plan_Date__c, Last_Spend_Date__c FROM Account WHERE Total_Plans_Sold__c >= 7 AND EEP_Firm_ID__c != null${EXCLUDE_DEACTIVATED_ACCOUNTS}`,
    signal,
  );

  const oppRecords = await executeQuery<{
    AccountId: string;
    Plan_Created_Date__c: string | null;
    Submitted_Date__c: string | null;
  }>(
    `SELECT AccountId, Plan_Created_Date__c, Submitted_Date__c FROM Opportunity WHERE Account.Total_Plans_Sold__c >= 7 AND Account.EEP_Firm_ID__c != null AND (Plan_Created_Date__c != null OR Submitted_Date__c != null)${EXCLUDE_DEACTIVATED_ACCOUNTS_REL}`,
    signal,
  );

  const oppDateMap = new Map<string, { maxPlanCreated: string | null; maxSubmitted: string | null }>();
  for (const opp of oppRecords) {
    if (!opp.AccountId) continue;
    const existing = oppDateMap.get(opp.AccountId);
    if (existing) {
      existing.maxPlanCreated = latestDate([existing.maxPlanCreated, opp.Plan_Created_Date__c]);
      existing.maxSubmitted = latestDate([existing.maxSubmitted, opp.Submitted_Date__c]);
    } else {
      oppDateMap.set(opp.AccountId, {
        maxPlanCreated: opp.Plan_Created_Date__c,
        maxSubmitted: opp.Submitted_Date__c,
      });
    }
  }

  const results: Record<string, unknown>[] = [];

  for (const r of accounts) {
    const oppDates = oppDateMap.get(r.Id);
    const lastActivity = latestDate([
      r.Last_Plan_Date__c,
      r.Last_Spend_Date__c,
      oppDates?.maxPlanCreated ?? null,
      oppDates?.maxSubmitted ?? null,
    ]);
    if (!lastActivity) continue;

    // Churn date = last activity + 180 days
    const activityDate = new Date(lastActivity);
    activityDate.setDate(activityDate.getDate() + CHURN_DAYS);
    const churnDate = activityDate.toISOString().slice(0, 10);

    const today = new Date().toISOString().slice(0, 10);
    if (churnDate >= startDate && churnDate <= today) {
      results.push({ Id: r.Id, Name: r.Name, computed_churn_date: churnDate });
    }
  }

  return results;
}

// ── Stale Users ──

export async function fetchStaleUsers(signal?: AbortSignal): Promise<StaleUsersData> {
  return cachedQuery('retention_staleusers', async () => {
    const records = await executeQuery<{
      Id: string;
      FirstName: string | null;
      LastName: string | null;
      Email: string | null;
      Last_Login__c: string;
      Account: { Name: string; EEP_Firm_ID__c: string };
    }>(
      `SELECT Id, FirstName, LastName, Email, Last_Login__c, Account.Name, Account.EEP_Firm_ID__c FROM Contact WHERE Account.EEP_Firm_ID__c != null AND Last_Login__c != null AND Last_Login__c < LAST_N_DAYS:45${EXCLUDE_DEMO_DEACTIVATED_USERS}`,
      signal
    );

    const users: StaleUser[] = records
      .map((r) => ({
        id: r.Id,
        name: [r.FirstName, r.LastName].filter(Boolean).join(' ') || '(No Name)',
        email: r.Email || '',
        accountName: r.Account?.Name || '',
        lastLogin: r.Last_Login__c,
        daysSinceLogin: daysSince(r.Last_Login__c),
      }))
      .sort((a, b) => b.daysSinceLogin - a.daysSinceLogin);

    return { totalStaleUsers: users.length, users };
  });
}

// ── Stale Accounts ──

export async function fetchStaleAccounts(signal?: AbortSignal): Promise<StaleAccountsData> {
  return cachedQuery('retention_staleaccounts', async () => {
    const records = await executeQuery<{
      AccountId: string;
      Account: { Name: string; EEP_Firm_ID__c: string };
      Last_Login__c: string;
      EEP_User_ID__c: string;
    }>(
      `SELECT AccountId, Account.Name, Account.EEP_Firm_ID__c, Last_Login__c, EEP_User_ID__c FROM Contact WHERE Account.EEP_Firm_ID__c != null AND EEP_User_ID__c != null AND Last_Login__c != null${EXCLUDE_DEMO_DEACTIVATED_USERS}`,
      signal
    );

    // Group by AccountId — find max login date + count users per account
    const accountMap = new Map<string, { name: string; maxLogin: string; userCount: number }>();

    for (const r of records) {
      const acctId = r.AccountId;
      if (!acctId) continue;
      const existing = accountMap.get(acctId);
      if (existing) {
        existing.userCount++;
        if (r.Last_Login__c > existing.maxLogin) {
          existing.maxLogin = r.Last_Login__c;
        }
      } else {
        accountMap.set(acctId, {
          name: r.Account?.Name || '',
          maxLogin: r.Last_Login__c,
          userCount: 1,
        });
      }
    }

    // Filter for accounts where most recent login >= 45 days ago
    const accounts: StaleAccount[] = [];
    for (const [accountId, data] of accountMap) {
      const days = daysSince(data.maxLogin);
      if (days >= 45) {
        accounts.push({
          accountId,
          accountName: data.name,
          lastLogin: data.maxLogin,
          totalUsers: data.userCount,
          daysSinceLogin: days,
        });
      }
    }

    accounts.sort((a, b) => b.daysSinceLogin - a.daysSinceLogin);

    return { totalStaleAccounts: accounts.length, accounts };
  });
}
